import type { ResultOf } from '@graphql-typed-document-node/core';
import { stagedHouseholdSchema } from 'src/features/advisor-book-onboarding/utils/staged-book';
import { graphql } from 'src/gql';
import { z } from 'zod';

export const ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_QUERY = graphql(/* GraphQL */ `
  query AdvisorOnboardingStagedHousehold($id: ID!) {
    advisorOnboardingStagedHousehold(id: $id) {
      id
      uuid
      draft
      householdId
      householdUuid
      externalKey
      householdName
      estAum
      importErrors
      displayName
      status
      blockedReason
      enrollmentUuid
      stagedClients {
        id
        role
        firstName
        lastName
        email
        phone
        importErrors
      }
      stagedAccounts {
        id
        number
        accountName
        accountType
        custodian
        transferMethod
        beneficiaries {
          designation
          kind
          firstName
          middleName
          lastName
          trustEntityName
          relationship
          dateOfBirthOrTrustDate
          taxId
          percentage
          perStirpes
        }
        estimatedBalance
        typeGroup
        registrationType
        hasTransferOnDeathDesignation
        importErrors
        standingInstructions {
          id
          bankName
          maskedAccountNumber
          shouldCarryOver
        }
        recurringTransfers {
          id
          kind
          frequency
          amountCents
          dayOfMonth
          dayOfWeek
          fromStandingInstructionId
          toStandingInstructionId
          shouldCarryOver
        }
        trustInformation {
          entityName
          taxIdType
          taxId
          countryOfOrganizationCode
          trustDate
          trustType
          trustRevocability
          accreditedInvestor
          registeredBrokerDealer
          registeredInvestmentAdvisor
          registeredInvestmentCompany
          homeAddressLine1
          homeAddressLine2
          homeCity
          homeState
          homePostalCode
          mailingAddressSameAsHome
          mailingAddressLine1
          mailingAddressLine2
          mailingCity
          mailingState
          mailingPostalCode
        }
        employerInformation {
          entityName
          taxId
        }
        accountStakeholders {
          role
          user {
            id
          }
        }
        jointOwners {
          fullName
          relationshipToPrimary
          ownershipPercentage
        }
      }
    }
  }
`);

export const CONFIRM_ADVISOR_ONBOARDING_ACCOUNTS_MUTATION = graphql(/* GraphQL */ `
  mutation ConfirmAdvisorOnboardingAccounts($input: ConfirmAccountsInput!) {
    confirmAdvisorOnboardingAccounts(input: $input) {
      success
      errors {
        field
        message
      }
      stagedHousehold {
        id
        status
      }
    }
  }
`);

const confirmAccountsResultSchema = z
  .object({
    success: z.boolean(),
    errors: z
      .array(
        z.object({
          field: z.string().nullable(),
          message: z.string(),
        }),
      )
      .nullable(),
    stagedHousehold: z
      .object({
        id: z.string(),
        status: z.string(),
      })
      .nullable(),
  })
  .readonly();

const confirmStagedHouseholdSchema = stagedHouseholdSchema
  .extend({
    uuid: z.string().uuid(),
    householdId: z.string().nullable(),
    householdUuid: z.string().nullable(),
    status: z.string(),
    enrollmentUuid: z.string().uuid(),
    blockedReason: z.string().nullable(),
  })
  .readonly();

export type ConfirmAccountsStagedHousehold = z.infer<typeof confirmStagedHouseholdSchema>;

export type AdvisorOnboardingStagedHouseholdLoad =
  | { readonly status: 'pending' }
  | { readonly status: 'missing' }
  | { readonly status: 'invalid'; readonly reason: string }
  | { readonly status: 'ok'; readonly household: ConfirmAccountsStagedHousehold };

const INVALID_STAGED_DATA_REASON =
  'This household has staged data we could not read. Fix the import or contact support.';

export function resolveAdvisorOnboardingStagedHousehold(
  data: ResultOf<typeof ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_QUERY> | undefined,
): AdvisorOnboardingStagedHouseholdLoad {
  if (data === undefined) {
    return { status: 'pending' };
  }

  const household = data.advisorOnboardingStagedHousehold;
  if (household === null) {
    return { status: 'missing' };
  }

  const parsed = confirmStagedHouseholdSchema.safeParse(household);
  if (!parsed.success) {
    return { status: 'invalid', reason: INVALID_STAGED_DATA_REASON };
  }

  return { status: 'ok', household: parsed.data };
}

export function getAdvisorOnboardingStagedHousehold(
  data: ResultOf<typeof ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_QUERY> | undefined,
): ConfirmAccountsStagedHousehold | null | undefined {
  const resolved = resolveAdvisorOnboardingStagedHousehold(data);
  switch (resolved.status) {
    case 'pending':
      return undefined;
    case 'missing':
      return null;
    case 'invalid':
      return undefined;
    case 'ok':
      return resolved.household;
  }
}

export function confirmAdvisorOnboardingAccountsSucceeded(
  data: ResultOf<typeof CONFIRM_ADVISOR_ONBOARDING_ACCOUNTS_MUTATION> | null | undefined,
): boolean {
  if (data === null || data === undefined) {
    return false;
  }

  const payload = data.confirmAdvisorOnboardingAccounts;
  if (payload === null) {
    return false;
  }

  const parsed = confirmAccountsResultSchema.safeParse(payload);
  return parsed.success && parsed.data.success;
}

export function getConfirmAdvisorOnboardingAccountsErrors(
  data: ResultOf<typeof CONFIRM_ADVISOR_ONBOARDING_ACCOUNTS_MUTATION> | null | undefined,
): readonly { readonly field: string | null; readonly message: string }[] {
  if (data === null || data === undefined) {
    return [];
  }

  const payload = data.confirmAdvisorOnboardingAccounts;
  if (payload === null || payload.errors === null) {
    return [];
  }

  return payload.errors;
}
